import { useMemo } from "react";
import { Check, ChevronsUpDown, Eye, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { VIEW_AS_ROLES, type ViewAsRole } from "@/config/view-as";
import { useProfile } from "@/context/ProfileContext";
import { useTeamScope } from "@/context/TeamScopeContext";
import { useViewAs } from "@/context/ViewAsContext";
import { useSalesTeams } from "@/hooks/queries/use-sales-teams";
import { useIsDvhBusiness } from "@/hooks/use-business-vertical";
import { cn } from "@/lib/utils";

/** The top-bar chip: the look of the workspace scope chip, tokens only. */
const chipClassName = cn(
  "inline-flex h-10 max-w-[260px] items-center gap-2 rounded-g-md border border-g-line bg-g-bg-card px-3 text-[13px] font-semibold text-g-ink-700 transition-colors duration-150 ease-out",
  "hover:bg-g-primary-wash hover:text-g-ink-900 data-[state=open]:bg-g-primary-wash",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
);

/**
 * The sketch's "Visa som" switch in the top bar: an owner or admin previews
 * the dashboard as the department head or the employee sees it. The preview
 * narrows the nav and swaps the home page; it never changes what the server
 * allows, which the tooltip says. In the head preview a second chip picks
 * the department (bound to the team scope). Renders nothing for everyone
 * else.
 */
export function ViewAsSwitch({ className }: { className?: string }) {
  const { t } = useTranslation("shell");
  const { canPreview, effectiveRole, setViewAs } = useViewAs();
  const { businessProfile, memberAccessLevel } = useProfile();
  const { activeTeamId, setActiveTeamId } = useTeamScope();
  const isDvh = useIsDvhBusiness();
  const businessId = businessProfile?.uid;
  const teamsQuery = useSalesTeams(
    canPreview && effectiveRole === "head" && memberAccessLevel !== "none" ? businessId : undefined,
  );
  const departments = useMemo(() => {
    const teams = (teamsQuery.data ?? []) as Array<{
      id: string;
      name: string;
      parentTeamId?: string | null;
    }>;
    const children = isDvh ? teams.filter((tm) => tm.parentTeamId) : [];
    return children.length > 0 ? children : teams;
  }, [teamsQuery.data, isDvh]);

  if (!canPreview) return null;

  const activeTeam = departments.find((tm) => tm.id === activeTeamId) ?? null;

  return (
    <div className={cn("flex items-center gap-2", className)} data-testid="view-as">
      <TooltipProvider delayDuration={300}>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="view-as-chip"
                  aria-label={`${t("viewAs.label")}: ${t(`viewAs.roles.${effectiveRole}`)}`}
                  className={chipClassName}
                >
                  <Eye className="size-4 shrink-0 text-g-ink-400" aria-hidden="true" />
                  <span className="shrink-0 font-medium text-g-ink-500">{t("viewAs.label")}</span>
                  <span className="min-w-0 truncate">{t(`viewAs.roles.${effectiveRole}`)}</span>
                  <ChevronsUpDown className="size-4 shrink-0 text-g-ink-400" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t("viewAs.tooltip")}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent className="min-w-56 rounded-lg" side="bottom" align="end" sideOffset={4}>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("viewAs.menuLabel")}
            </DropdownMenuLabel>
            {VIEW_AS_ROLES.map((role: ViewAsRole) => {
              const active = role === effectiveRole;
              return (
                <DropdownMenuItem
                  key={role}
                  role="menuitemradio"
                  aria-checked={active}
                  className="gap-2"
                  onSelect={() => setViewAs(role)}
                >
                  <span className="truncate">{t(`viewAs.roles.${role}`)}</span>
                  {active ? <Check className="ml-auto size-4 shrink-0" aria-hidden="true" /> : null}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipProvider>

      {effectiveRole === "head" ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="view-as-team"
              aria-label={`${t("viewAs.teamLabel")}: ${activeTeam?.name ?? t("viewAs.noTeams")}`}
              className={chipClassName}
            >
              <Users className="size-4 shrink-0 text-g-ink-400" aria-hidden="true" />
              <span className="min-w-0 truncate">
                {activeTeam?.name ?? departments[0]?.name ?? t("viewAs.noTeams")}
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-g-ink-400" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-56 rounded-lg" side="bottom" align="end" sideOffset={4}>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("viewAs.teamMenuLabel")}
            </DropdownMenuLabel>
            {departments.length === 0 ? (
              <DropdownMenuItem disabled>{t("viewAs.noTeams")}</DropdownMenuItem>
            ) : (
              departments.map((tm) => (
                <DropdownMenuItem
                  key={tm.id}
                  role="menuitemradio"
                  aria-checked={tm.id === activeTeamId}
                  className="gap-2"
                  onSelect={() => setActiveTeamId(tm.id)}
                >
                  <span className="truncate">{tm.name}</span>
                  {tm.id === activeTeamId ? (
                    <Check className="ml-auto size-4 shrink-0" aria-hidden="true" />
                  ) : null}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
